import React, { useState, useCallback, useMemo, useEffect, ChangeEvent } from 'react';
import { format, eachDayOfInterval, parseISO, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import {
  Campaign,
  CampaignDetail,
  TemplateMode,
  HsmTemplate
} from '../../types';
import { CampaignRestartRequest } from '../../services/hsm-campaigns.service';
import {
  brazilianToISO,
  isoToBrazilian,
  isValidBrazilianDate,
  applyDateMask
} from '../../utils/date';

// Helper to get placeholder numbers from template body
function getTemplatePlaceholders(template: HsmTemplate): number[] {
  const bodyComponent = template.components?.find(c => c.type === 'BODY');
  const bodyText = bodyComponent?.text || '';
  const matches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
  const uniquePlaceholders = [...new Set(matches.map(m => parseInt(m.replace(/\{\{|\}\}/g, ''), 10)))];
  return uniquePlaceholders.sort((a, b) => a - b);
}

// Helper to calculate campaign duration in days
function getCampaignDuration(startDate: string, endDate: string): number {
  return differenceInDays(parseISO(endDate), parseISO(startDate)) + 1;
}

export interface RestartCampaignDialogProps {
  open: boolean;
  campaign: Campaign | CampaignDetail | null;
  approvedTemplates: HsmTemplate[];
  templatesLoading: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CampaignRestartRequest) => Promise<{ success: boolean; error?: string; contactsReset?: number }>;
  getTemplateVariableMapping: (templateName: string) => Promise<{ success: boolean; variableMapping?: string[] | null; error?: string }>;
}

export function RestartCampaignDialog({
  open,
  campaign,
  approvedTemplates,
  templatesLoading,
  onOpenChange,
  onSubmit,
  getTemplateVariableMapping
}: RestartCampaignDialogProps) {
  // Form state
  const [startDate, setStartDate] = useState(''); // ISO format
  const [startDateDisplay, setStartDateDisplay] = useState(''); // Brazilian format
  const [endDate, setEndDate] = useState(''); // ISO format
  const [endDateDisplay, setEndDateDisplay] = useState(''); // Brazilian format
  const [runTime, setRunTime] = useState('09:00');
  const [templateMode, setTemplateMode] = useState<TemplateMode>('SAME_FOR_ALL');
  const [defaultTemplateId, setDefaultTemplateId] = useState('');
  const [dayTemplates, setDayTemplates] = useState<Record<number, string>>({});
  const [defaultVariableMapping, setDefaultVariableMapping] = useState<string[]>([]);
  const [dayVariableMappings, setDayVariableMappings] = useState<Record<number, string[]>>({});
  const [defaultMappingAutoFetched, setDefaultMappingAutoFetched] = useState(false);
  const [dayMappingsAutoFetched, setDayMappingsAutoFetched] = useState<Record<number, boolean>>({});
  const [restarting, setRestarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate dates array for different per day mode
  const dateRange = useMemo(() => {
    if (!startDate || !endDate) return [];
    try {
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      if (start > end) return [];
      return eachDayOfInterval({ start, end });
    } catch {
      return [];
    }
  }, [startDate, endDate]);

  // Helper function to initialize variable mapping with default value
  const initializeVariableMapping = useCallback((placeholders: number[]): string[] => {
    return placeholders.map(() => 'Nome');
  }, []);

  // Reset form state
  const resetForm = useCallback(() => {
    setStartDate('');
    setStartDateDisplay('');
    setEndDate('');
    setEndDateDisplay('');
    setRunTime('09:00');
    setTemplateMode('SAME_FOR_ALL');
    setDefaultTemplateId('');
    setDayTemplates({});
    setDefaultVariableMapping([]);
    setDayVariableMappings({});
    setDefaultMappingAutoFetched(false);
    setDayMappingsAutoFetched({});
    setError(null);
  }, []);

  // Pre-fill form when campaign changes
  useEffect(() => {
    if (campaign && open) {
      // Pre-fill form with campaign's current configuration
      const newStartDateISO = format(new Date(), 'yyyy-MM-dd');
      setStartDate(newStartDateISO);
      setStartDateDisplay(isoToBrazilian(newStartDateISO));

      // Calculate the same duration as original campaign
      const originalDuration = getCampaignDuration(campaign.startDate, campaign.endDate);
      const newEndDate = new Date();
      newEndDate.setDate(newEndDate.getDate() + originalDuration - 1);
      const newEndDateISO = format(newEndDate, 'yyyy-MM-dd');
      setEndDate(newEndDateISO);
      setEndDateDisplay(isoToBrazilian(newEndDateISO));

      setRunTime(campaign.runTime || '09:00');
      setTemplateMode(campaign.templateMode);
      setDefaultTemplateId(campaign.defaultTemplateId || '');

      // Pre-fill day templates if DIFFERENT_PER_DAY mode and campaign has dayTemplates
      if (campaign.templateMode === 'DIFFERENT_PER_DAY' && 'dayTemplates' in campaign && campaign.dayTemplates) {
        const dayTemplatesMap: Record<number, string> = {};
        campaign.dayTemplates.forEach((dt) => {
          // dayNumber is 1-based, convert to 0-based index
          dayTemplatesMap[dt.dayNumber - 1] = dt.templateId;
        });
        setDayTemplates(dayTemplatesMap);
      } else {
        setDayTemplates({});
      }

      setError(null);
    }
  }, [campaign, open]);

  // Handle start date input change
  const handleStartDateChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const masked = applyDateMask(e.target.value);
    setStartDateDisplay(masked);

    if (isValidBrazilianDate(masked)) {
      setStartDate(brazilianToISO(masked));
    } else {
      setStartDate('');
    }
  }, []);

  // Handle end date input change
  const handleEndDateChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const masked = applyDateMask(e.target.value);
    setEndDateDisplay(masked);

    if (isValidBrazilianDate(masked)) {
      setEndDate(brazilianToISO(masked));
    } else {
      setEndDate('');
    }
  }, []);

  // Update default variable mapping when template changes
  const handleDefaultTemplateChange = useCallback(async (templateId: string) => {
    setDefaultTemplateId(templateId);
    const template = approvedTemplates.find(t => t.id === templateId);
    if (template) {
      const placeholders = getTemplatePlaceholders(template);

      // Try to fetch stored mapping from backend
      const mappingResult = await getTemplateVariableMapping(template.name);
      if (mappingResult.success && mappingResult.variableMapping && mappingResult.variableMapping.length > 0) {
        setDefaultVariableMapping(mappingResult.variableMapping);
        setDefaultMappingAutoFetched(true);
      } else {
        setDefaultVariableMapping(initializeVariableMapping(placeholders));
        setDefaultMappingAutoFetched(false);
      }
    } else {
      setDefaultVariableMapping([]);
      setDefaultMappingAutoFetched(false);
    }
  }, [approvedTemplates, initializeVariableMapping, getTemplateVariableMapping]);

  // Update day template and its variable mapping
  const handleDayTemplateChange = useCallback(async (dayIndex: number, templateId: string) => {
    setDayTemplates(prev => ({
      ...prev,
      [dayIndex]: templateId
    }));
    const template = approvedTemplates.find(t => t.id === templateId);
    if (template) {
      const placeholders = getTemplatePlaceholders(template);

      // Try to fetch stored mapping from backend
      const mappingResult = await getTemplateVariableMapping(template.name);
      if (mappingResult.success && mappingResult.variableMapping && mappingResult.variableMapping.length > 0) {
        setDayVariableMappings(prev => ({
          ...prev,
          [dayIndex]: mappingResult.variableMapping!
        }));
        setDayMappingsAutoFetched(prev => ({
          ...prev,
          [dayIndex]: true
        }));
      } else {
        setDayVariableMappings(prev => ({
          ...prev,
          [dayIndex]: initializeVariableMapping(placeholders)
        }));
        setDayMappingsAutoFetched(prev => ({
          ...prev,
          [dayIndex]: false
        }));
      }
    } else {
      setDayVariableMappings(prev => {
        const newMappings = { ...prev };
        delete newMappings[dayIndex];
        return newMappings;
      });
      setDayMappingsAutoFetched(prev => {
        const newFlags = { ...prev };
        delete newFlags[dayIndex];
        return newFlags;
      });
    }
  }, [approvedTemplates, initializeVariableMapping, getTemplateVariableMapping]);

  // Handle restart campaign confirmation
  const handleRestartConfirm = useCallback(async () => {
    if (!campaign) return;

    // Validation
    if (!startDate || !endDate) {
      setError('Datas de inicio e fim sao obrigatorias');
      return;
    }
    if (templateMode === 'SAME_FOR_ALL' && !defaultTemplateId) {
      setError('Selecione um template');
      return;
    }
    if (templateMode === 'DIFFERENT_PER_DAY') {
      const missingDays = dateRange.some((_, index) => !dayTemplates[index]);
      if (missingDays) {
        setError('Selecione um template para cada dia');
        return;
      }
    }

    setRestarting(true);
    setError(null);

    // Helper functions
    const getTemplateById = (templateId: string): HsmTemplate | undefined => {
      return approvedTemplates.find(t => t.id === templateId);
    };

    const getTemplateName = (templateId: string): string => {
      const template = getTemplateById(templateId);
      return template?.name || templateId;
    };

    const getTemplateLanguage = (templateId: string): string => {
      const template = getTemplateById(templateId);
      return template?.language || 'pt_BR';
    };

    const restartData: CampaignRestartRequest = {
      startDate,
      endDate,
      runTime,
      templateMode,
      defaultTemplateId: templateMode === 'SAME_FOR_ALL' ? defaultTemplateId : undefined,
      defaultTemplateName: templateMode === 'SAME_FOR_ALL' ? getTemplateName(defaultTemplateId) : undefined,
      defaultLanguageCode: templateMode === 'SAME_FOR_ALL' ? getTemplateLanguage(defaultTemplateId) : undefined,
      defaultVariableMapping: templateMode === 'SAME_FOR_ALL' && defaultVariableMapping.length > 0
        ? defaultVariableMapping
        : undefined,
      dayTemplates: templateMode === 'DIFFERENT_PER_DAY'
        ? dateRange.map((date, index) => ({
            dayNumber: index + 1,
            date: format(date, 'yyyy-MM-dd'),
            templateId: dayTemplates[index],
            templateName: getTemplateName(dayTemplates[index]),
            languageCode: getTemplateLanguage(dayTemplates[index]),
            variableMapping: dayVariableMappings[index]?.length > 0
              ? JSON.stringify(dayVariableMappings[index])
              : undefined
          }))
        : undefined
    };

    const result = await onSubmit(restartData);
    setRestarting(false);

    if (result.success) {
      onOpenChange(false);
      resetForm();
    } else {
      setError(result.error || 'Erro ao reiniciar campanha');
    }
  }, [
    campaign,
    startDate,
    endDate,
    runTime,
    templateMode,
    defaultTemplateId,
    dayTemplates,
    defaultVariableMapping,
    dayVariableMappings,
    dateRange,
    approvedTemplates,
    onSubmit,
    onOpenChange,
    resetForm
  ]);

  return (
    <Dialog open={open} onOpenChange={(openState) => {
      if (!restarting) {
        onOpenChange(openState);
        if (!openState) {
          resetForm();
        }
      }
    }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Reiniciar Campanha{campaign ? `: ${campaign.name}` : ''}</DialogTitle>
          <DialogDescription>
            Configure a campanha para reiniciar. Todos os contatos serao redefinidos para o dia 1 (exceto opt-outs). Os contatos inscritos serao mantidos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Date Range */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="restart-start-date">Data de Inicio *</Label>
              <Input
                id="restart-start-date"
                type="text"
                inputMode="numeric"
                value={startDateDisplay}
                onChange={handleStartDateChange}
                placeholder="DD/MM/AAAA"
                maxLength={10}
              />
              {startDateDisplay && !isValidBrazilianDate(startDateDisplay) && startDateDisplay.length === 10 && (
                <p className="text-xs text-red-500">Data invalida</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="restart-end-date">Data de Fim *</Label>
              <Input
                id="restart-end-date"
                type="text"
                inputMode="numeric"
                value={endDateDisplay}
                onChange={handleEndDateChange}
                placeholder="DD/MM/AAAA"
                maxLength={10}
              />
              {endDateDisplay && !isValidBrazilianDate(endDateDisplay) && endDateDisplay.length === 10 && (
                <p className="text-xs text-red-500">Data invalida</p>
              )}
            </div>
          </div>

          {dateRange.length > 0 && (
            <p className="text-sm text-muted-foreground">
              Duracao: {dateRange.length} dia{dateRange.length !== 1 ? 's' : ''}
            </p>
          )}

          {/* Run Time - 24h format */}
          <div className="space-y-2">
            <Label>Horario de Envio</Label>
            <div className="flex items-center gap-1">
              <select
                value={runTime.split(':')[0] || '09'}
                onChange={(e) => {
                  const minutes = runTime.split(':')[1] || '00';
                  setRunTime(`${e.target.value}:${minutes}`);
                }}
                className="flex h-9 w-16 rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map((hour) => (
                  <option key={hour} value={hour}>{hour}</option>
                ))}
              </select>
              <span className="text-lg font-medium">:</span>
              <select
                value={runTime.split(':')[1] || '00'}
                onChange={(e) => {
                  const hours = runTime.split(':')[0] || '09';
                  setRunTime(`${hours}:${e.target.value}`);
                }}
                className="flex h-9 w-16 rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {['00', '15', '30', '45'].map((minute) => (
                  <option key={minute} value={minute}>{minute}</option>
                ))}
              </select>
            </div>
            <p className="text-xs text-muted-foreground">
              Horario em que as mensagens serao enviadas
            </p>
          </div>

          {/* Template Mode */}
          <div className="space-y-2">
            <Label>Modo de Template *</Label>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
                <input
                  type="radio"
                  name="restartTemplateMode"
                  value="SAME_FOR_ALL"
                  checked={templateMode === 'SAME_FOR_ALL'}
                  onChange={() => setTemplateMode('SAME_FOR_ALL')}
                  className="h-4 w-4"
                />
                <div>
                  <div className="font-medium text-sm">Mesmo template para todos os dias</div>
                  <div className="text-xs text-muted-foreground">
                    Todos os contatos receberao o mesmo template durante a campanha.
                  </div>
                </div>
              </label>
              <label className="flex items-center gap-2 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
                <input
                  type="radio"
                  name="restartTemplateMode"
                  value="DIFFERENT_PER_DAY"
                  checked={templateMode === 'DIFFERENT_PER_DAY'}
                  onChange={() => setTemplateMode('DIFFERENT_PER_DAY')}
                  className="h-4 w-4"
                />
                <div>
                  <div className="font-medium text-sm">Template diferente por dia</div>
                  <div className="text-xs text-muted-foreground">
                    Configure um template diferente para cada dia da campanha.
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Template Selection */}
          {templateMode === 'SAME_FOR_ALL' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="restart-default-template">Template *</Label>
                <select
                  id="restart-default-template"
                  value={defaultTemplateId}
                  onChange={(e) => handleDefaultTemplateChange(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  disabled={templatesLoading}
                >
                  <option value="">Selecione um template</option>
                  {approvedTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({template.language})
                    </option>
                  ))}
                </select>
                {approvedTemplates.length === 0 && !templatesLoading && (
                  <p className="text-xs text-muted-foreground">
                    Nenhum template aprovado disponivel. Crie e aguarde a aprovacao de templates na aba Templates.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Day-by-day Template Selection */}
          {templateMode === 'DIFFERENT_PER_DAY' && dateRange.length > 0 && (
            <div className="space-y-2">
              <Label>Templates por Dia *</Label>
              <div className="max-h-80 overflow-y-auto space-y-3 border rounded-lg p-3">
                {dateRange.map((date, index) => {
                  return (
                    <div key={index} className="space-y-2 pb-3 border-b last:border-b-0 last:pb-0">
                      <div className="flex items-center gap-3">
                        <div className="min-w-[100px] text-sm">
                          <div className="font-medium">Dia {index + 1}</div>
                          <div className="text-xs text-muted-foreground">
                            {format(date, 'dd/MM', { locale: ptBR })}
                          </div>
                        </div>
                        <select
                          value={dayTemplates[index] || ''}
                          onChange={(e) => handleDayTemplateChange(index, e.target.value)}
                          className="flex h-9 flex-1 rounded-md border border-input bg-background px-3 py-1 text-sm"
                          disabled={templatesLoading}
                        >
                          <option value="">Selecione...</option>
                          {approvedTemplates.map((template) => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {templateMode === 'DIFFERENT_PER_DAY' && dateRange.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Selecione as datas de inicio e fim para configurar os templates por dia.
            </p>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              resetForm();
            }}
            disabled={restarting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleRestartConfirm}
            disabled={restarting}
          >
            {restarting ? 'Reiniciando...' : 'Reiniciar Campanha'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
